import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    Checkbox,
    Chip,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Typography
} from '@mui/material';
import {
    Send as SendIcon,
    ArrowBack as ArrowBackIcon,
    PersonSearch as PersonSearchIcon,
    Draw as DrawIcon,
    Clear as ClearIcon,
    DoneAll as DoneAllIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { initializeDB } from '../../../config/db';
import { JOB_STORE, STATUS_DRAFT, STATUS_SUBMITTED } from '../../../config/constants';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';
import { generateUUID } from '../../../utils/uuid';
import { welderQualificationReleaseService } from '../../../services/welderQualificationReleaseService';
import {
    DOCUMENTS_ITEMS,
    FIELD_VALIDATION_ITEMS,
    PRODUCTION_CONTROL_ITEMS,
    WELDING_PROCESS_OPTIONS,
    GOVERNING_REQUIREMENTS,
    GOVERNING_NOTE,
    BANNER_TEXT,
    STOP_WORK_TEXT,
    FIELD_RULE_TEXT,
    TOTAL_CHECKS,
    emptyChecklist,
    countChecked
} from './welderQualificationReleaseSchema';

/**
 * Имя человека: можно вписать руками (субподрядные сварщики) либо выбрать
 * из справочника сотрудников. Ручной ввод сбрасывает привязку к employee.
 */
const PersonField = ({ label, value, source, onTextChange, onPick, disabled, required, error, helperText }) => (
    <TextField
        fullWidth
        size="small"
        label={label}
        value={value || ''}
        onChange={(e) => onTextChange(e.target.value)}
        disabled={disabled}
        required={required}
        error={error}
        helperText={helperText}
        placeholder="Type a name or pick from employees"
        InputProps={{
            endAdornment: (
                <InputAdornment position="end">
                    {source === 'employee' && (
                        <Chip label="Employee" size="small" sx={{ height: 18, fontSize: '0.65rem', mr: 0.5 }} />
                    )}
                    {!disabled && (
                        <IconButton onClick={onPick} size="small" title="Select from employees">
                            <PersonSearchIcon fontSize="small" />
                        </IconButton>
                    )}
                </InputAdornment>
            )
        }}
    />
);

/** Секция чек-листа с кнопкой «отметить все» и счётчиком. */
const ChecklistSection = ({ number, title, items, values, onToggle, onCheckAll, disabled, columns = 1 }) => {
    const done = countChecked(values, items);

    return (
        <Card variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                    {number}. {title}
                </Typography>
                <Chip
                    label={`${done}/${items.length}`}
                    size="small"
                    color={done === items.length ? 'success' : 'default'}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                />
                {!disabled && done < items.length && (
                    <Button
                        size="small"
                        startIcon={<DoneAllIcon />}
                        onClick={onCheckAll}
                        sx={{ ml: 'auto' }}
                    >
                        Check all
                    </Button>
                )}
            </Box>

            <Grid container spacing={0}>
                {items.map((item) => (
                    <Grid item xs={12} md={12 / columns} key={item.key}>
                        <FormControlLabel
                            sx={{ alignItems: 'flex-start', mr: 0, mb: 0.5 }}
                            control={
                                <Checkbox
                                    checked={!!values?.[item.key]}
                                    onChange={(e) => onToggle(item.key, e.target.checked)}
                                    disabled={disabled}
                                    size="small"
                                    sx={{ pt: 0.5 }}
                                />
                            }
                            label={<Typography variant="body2">{item.label}</Typography>}
                        />
                    </Grid>
                ))}
            </Grid>
        </Card>
    );
};

const WelderQualificationReleaseForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const uuid = searchParams.get('uuid');

    const [jobs, setJobs] = useState([]);
    const [jobsLoading, setJobsLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [signatureField, setSignatureField] = useState(null);   // 'qcCwi' | 'welder'
    const [employeeTarget, setEmployeeTarget] = useState(null);   // 'qcCwi' | 'welder'
    const sigPadRef = useRef(null);
    const saveTimeoutRef = useRef(null);

    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        jobCode: '',
        jobName: '',
        jobLocation: '',
        releaseDate: dayjs().format('YYYY-MM-DD'),

        documentsChecklist: emptyChecklist(DOCUMENTS_ITEMS),
        fieldValidationChecklist: emptyChecklist(FIELD_VALIDATION_ITEMS),
        productionControls: emptyChecklist(PRODUCTION_CONTROL_ITEMS),

        welderName: '',
        welderEmployeeId: '',
        welderSource: 'manual',
        welderStampId: '',
        wpsNo: '',
        weldingProcess: '',

        qcCwiName: '',
        qcCwiEmployeeId: '',
        qcCwiSource: 'manual',
        releasedAt: dayjs().format('YYYY-MM-DDTHH:mm'),

        qcCwiSignature: '',
        qcCwiPrintName: '',
        welderSignature: '',
        welderPrintName: '',

        status: STATUS_DRAFT
    });

    const doneChecks =
        countChecked(form.documentsChecklist, DOCUMENTS_ITEMS) +
        countChecked(form.fieldValidationChecklist, FIELD_VALIDATION_ITEMS) +
        countChecked(form.productionControls, PRODUCTION_CONTROL_ITEMS);

    useEffect(() => {
        loadJobs();
    }, []);

    useEffect(() => {
        if (uuid) loadFormData();
    }, [uuid]);

    const loadJobs = async () => {
        try {
            const db = await initializeDB();
            const tx = db.transaction(JOB_STORE, 'readonly');
            const store = tx.objectStore(JOB_STORE);
            const allJobs = await store.getAll();
            const sortedJobs = (allJobs || []).sort((a, b) => (b.Job || '').localeCompare(a.Job || ''));
            setJobs(sortedJobs);
        } catch (error) {
            console.error('Error loading jobs:', error);
        } finally {
            setJobsLoading(false);
        }
    };

    const loadFormData = async () => {
        try {
            const data = await welderQualificationReleaseService.getFromIndexedDB(uuid);
            if (data) {
                setForm((prev) => ({ ...prev, ...data }));
                setSubmitted(data.status === STATUS_SUBMITTED);
            }
        } catch (error) {
            console.error('Error loading form data:', error);
        }
    };

    const queueSave = (updatedForm) => {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

        saveTimeoutRef.current = setTimeout(async () => {
            try {
                await welderQualificationReleaseService.saveToIndexedDB(updatedForm);
            } catch (error) {
                console.error('Error saving to IndexedDB:', error);
            }
        }, 500);
    };

    const update = (patch) => {
        setForm((prev) => {
            const updated = { ...prev, ...patch };
            queueSave(updated);
            return updated;
        });
    };

    const handleJobChange = (event, newValue) => {
        update({
            jobCode: newValue?.Job || '',
            jobName: newValue?.JobName || '',
            jobLocation: newValue?.location || newValue?.Location || ''
        });
    };

    const toggleCheck = (listKey, itemKey, checked) => {
        setForm((prev) => {
            const updated = { ...prev, [listKey]: { ...(prev[listKey] || {}), [itemKey]: checked } };
            queueSave(updated);
            return updated;
        });
    };

    const checkAll = (listKey, items) => {
        setForm((prev) => {
            const all = items.reduce((acc, item) => ({ ...acc, [item.key]: true }), {});
            const updated = { ...prev, [listKey]: { ...(prev[listKey] || {}), ...all } };
            queueSave(updated);
            return updated;
        });
    };

    // ─── Люди: ручной ввод сбрасывает привязку к сотруднику ────────────
    const handlePersonText = (role, text) => {
        update({
            [`${role}Name`]: text,
            [`${role}EmployeeId`]: '',
            [`${role}Source`]: 'manual'
        });
    };

    const handleEmployeeSelect = (employee) => {
        if (employee && employeeTarget) {
            const fullName = employee.FullName || employee.fullName || '';
            update({
                [`${employeeTarget}Name`]: fullName,
                [`${employeeTarget}EmployeeId`]: String(employee.id ?? employee.EmployeeId ?? ''),
                [`${employeeTarget}Source`]: 'employee',
                // Print name подставляем, только если поле ещё пустое —
                // вручную поправленное имя не затираем.
                [`${employeeTarget}PrintName`]: form[`${employeeTarget}PrintName`] || fullName
            });
        }
        setEmployeeTarget(null);
    };

    const handleSignatureAccept = (signatureData) => {
        let signature = signatureData;
        if (typeof signatureData !== 'string') {
            signature = sigPadRef.current?.toDataURL() || '';
        }

        if (signatureField) {
            update({
                [`${signatureField}Signature`]: signature,
                [`${signatureField}PrintName`]: form[`${signatureField}PrintName`] || form[`${signatureField}Name`] || ''
            });
        }

        setSignatureField(null);
    };

    const handleSubmit = async () => {
        const newErrors = {};

        if (!form.jobCode) newErrors.jobCode = 'Job is required';
        if (!form.releaseDate) newErrors.releaseDate = 'Date is required';
        if (!form.welderName?.trim()) newErrors.welderName = 'Welder name is required';
        if (!form.qcCwiName?.trim()) newErrors.qcCwiName = 'QC/CWI name is required';
        if (!form.qcCwiSignature) newErrors.qcCwiSignature = 'QC/CWI signature is required';
        if (!form.welderSignature) newErrors.welderSignature = 'Welder signature is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            Swal.fire({
                title: 'Validation Error',
                text: 'Please fill in all required fields',
                icon: 'error',
                confirmButtonText: 'OK'
            });
            return;
        }

        // Незакрытые пункты — не блокируем, но заставляем подтвердить осознанно:
        // такая форма фиксирует stop-work, а не выпуск сварщика.
        const openItems = TOTAL_CHECKS - doneChecks;
        const confirm = await Swal.fire({
            title: 'Submit Form?',
            html: openItems > 0
                ? `<b>${openItems} of ${TOTAL_CHECKS} checks are not completed.</b><br/>` +
                  'The welder will NOT be released to weld. Submit anyway?'
                : 'Once submitted, the form cannot be edited',
            icon: openItems > 0 ? 'warning' : 'question',
            showCancelButton: true,
            confirmButtonColor: openItems > 0 ? '#d33' : '#3085d6',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, submit it'
        });

        if (!confirm.isConfirmed) return;

        setErrors({});
        setSaving(true);

        try {
            await welderQualificationReleaseService.submit({
                ...form,
                // Отдаём ISO с оффсетом: сервер живёт в другой зоне, а время
                // релиза должно остаться местным (Каролина).
                releasedAt: form.releasedAt ? dayjs(form.releasedAt).format() : null,
                status: STATUS_SUBMITTED
            });

            // Отправленную форму убираем с устройства — дальше она живёт в Symfony.
            await welderQualificationReleaseService.deleteFromIndexedDB(form.uuid);

            await Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Welder Qualification & Release submitted successfully'
            });

            navigate('/operations/welder-qualification-release');
        } catch (error) {
            console.error('Error submitting form:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to submit form'
            });
        } finally {
            setSaving(false);
        }
    };

    const renderSignature = (role, label) => (
        <Box>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                {form[`${role}Signature`] ? (
                    <>
                        <Box
                            component="img"
                            src={form[`${role}Signature`]}
                            alt={label}
                            sx={{
                                maxWidth: 200,
                                maxHeight: 60,
                                border: '1px solid #ddd',
                                borderRadius: 1,
                                cursor: submitted ? 'default' : 'pointer',
                                flexGrow: 1
                            }}
                            onClick={() => !submitted && setSignatureField(role)}
                        />
                        {!submitted && (
                            <IconButton size="small" onClick={() => update({ [`${role}Signature`]: '' })}>
                                <ClearIcon />
                            </IconButton>
                        )}
                    </>
                ) : (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DrawIcon />}
                        onClick={() => setSignatureField(role)}
                        fullWidth
                        disabled={submitted}
                        color={errors[`${role}Signature`] ? 'error' : 'primary'}
                    >
                        Sign
                    </Button>
                )}
            </Box>
        </Box>
    );

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Fixed Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/operations/welder-qualification-release')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography variant="h6" fontWeight={600} lineHeight={1.2}>
                                On-Site Welder Qualification &amp; Release
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Radial large bore structural pipe / tubular field welding
                            </Typography>
                        </Box>
                    </Box>
                    <Chip
                        label={`${doneChecks}/${TOTAL_CHECKS} checks`}
                        color={doneChecks === TOTAL_CHECKS ? 'success' : 'warning'}
                        size="small"
                    />
                </Box>

                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={8}>
                        <Autocomplete
                            options={jobs}
                            getOptionLabel={(option) => `${option.Job || ''} - ${option.JobName || ''}`}
                            value={jobs.find((j) => j.Job === form.jobCode) || null}
                            onChange={handleJobChange}
                            loading={jobsLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Job"
                                    size="small"
                                    error={!!errors.jobCode}
                                    helperText={errors.jobCode}
                                    required
                                />
                            )}
                            disabled={submitted}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Date"
                            value={form.releaseDate || ''}
                            onChange={(e) => update({ releaseDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.releaseDate}
                            helperText={errors.releaseDate}
                            required
                            disabled={submitted}
                            onClick={(e) => e.target.showPicker && e.target.showPicker()}
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Scrollable Content */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Alert
                            severity="warning"
                            icon={false}
                            sx={{
                                justifyContent: 'center',
                                fontWeight: 700,
                                backgroundColor: '#3c3c3c',
                                color: '#fff'
                            }}
                        >
                            {BANNER_TEXT}
                        </Alert>
                    </Grid>

                    <Grid item xs={12}>
                        <ChecklistSection
                            number={1}
                            title="Documents Required Before Welder Release"
                            items={DOCUMENTS_ITEMS}
                            values={form.documentsChecklist}
                            onToggle={(key, checked) => toggleCheck('documentsChecklist', key, checked)}
                            onCheckAll={() => checkAll('documentsChecklist', DOCUMENTS_ITEMS)}
                            disabled={submitted}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <ChecklistSection
                            number={2}
                            title="Field Validation / Site Test"
                            items={FIELD_VALIDATION_ITEMS}
                            values={form.fieldValidationChecklist}
                            onToggle={(key, checked) => toggleCheck('fieldValidationChecklist', key, checked)}
                            onCheckAll={() => checkAll('fieldValidationChecklist', FIELD_VALIDATION_ITEMS)}
                            disabled={submitted}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <ChecklistSection
                            number={3}
                            title="Production Controls"
                            items={PRODUCTION_CONTROL_ITEMS}
                            values={form.productionControls}
                            onToggle={(key, checked) => toggleCheck('productionControls', key, checked)}
                            onCheckAll={() => checkAll('productionControls', PRODUCTION_CONTROL_ITEMS)}
                            disabled={submitted}
                            columns={2}
                        />
                    </Grid>

                    {/* 4. Governing Requirements — справочный текст стандарта */}
                    <Grid item xs={12}>
                        <Card variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                                4. Governing Requirements
                            </Typography>
                            {GOVERNING_REQUIREMENTS.map((req) => (
                                <Typography variant="body2" key={req.lead} sx={{ mb: 0.5 }}>
                                    <strong>{req.lead}</strong> {req.text}
                                </Typography>
                            ))}
                            <Typography variant="caption" color="text.secondary" fontStyle="italic">
                                {GOVERNING_NOTE}
                            </Typography>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Alert
                            severity="error"
                            icon={false}
                            sx={{ backgroundColor: '#3c3c3c', color: '#e8b98a' }}
                        >
                            <strong style={{ textTransform: 'uppercase', textDecoration: 'underline' }}>Stop Work:</strong>
                            {' '}{STOP_WORK_TEXT}
                        </Alert>
                    </Grid>

                    {/* LC-36A Site Release */}
                    <Grid item xs={12}>
                        <Card variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                                LC-36A Site Release
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <PersonField
                                        label="Welder Name"
                                        value={form.welderName}
                                        source={form.welderSource}
                                        onTextChange={(text) => handlePersonText('welder', text)}
                                        onPick={() => setEmployeeTarget('welder')}
                                        disabled={submitted}
                                        required
                                        error={!!errors.welderName}
                                        helperText={errors.welderName}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Welder ID"
                                        value={form.welderStampId || ''}
                                        onChange={(e) => update({ welderStampId: e.target.value })}
                                        disabled={submitted}
                                        placeholder="Unique ID / stamp"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="WPS No."
                                        value={form.wpsNo || ''}
                                        onChange={(e) => update({ wpsNo: e.target.value })}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        freeSolo
                                        options={WELDING_PROCESS_OPTIONS}
                                        value={form.weldingProcess || ''}
                                        onChange={(e, newValue) => update({ weldingProcess: newValue || '' })}
                                        onInputChange={(e, newValue) => update({ weldingProcess: newValue || '' })}
                                        disabled={submitted}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Process" size="small" />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <PersonField
                                        label="QC/CWI Release"
                                        value={form.qcCwiName}
                                        source={form.qcCwiSource}
                                        onTextChange={(text) => handlePersonText('qcCwi', text)}
                                        onPick={() => setEmployeeTarget('qcCwi')}
                                        disabled={submitted}
                                        required
                                        error={!!errors.qcCwiName}
                                        helperText={errors.qcCwiName}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="datetime-local"
                                        label="Date / Time"
                                        value={form.releasedAt || ''}
                                        onChange={(e) => update({ releasedAt: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                        disabled={submitted}
                                        onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    {renderSignature('qcCwi', 'QC/CWI Signature')}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {renderSignature('welder', 'Welder Signature')}
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="QC/CWI Print Name"
                                        value={form.qcCwiPrintName || ''}
                                        onChange={(e) => update({ qcCwiPrintName: e.target.value })}
                                        disabled={submitted}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Welder Print Name"
                                        value={form.welderPrintName || ''}
                                        onChange={(e) => update({ welderPrintName: e.target.value })}
                                        disabled={submitted}
                                    />
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="caption" fontWeight={700} display="block" textAlign="center">
                            {FIELD_RULE_TEXT}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* Fixed Submit Button */}
            {!submitted && (
                <Box sx={{ backgroundColor: 'white', borderTop: '1px solid #e0e0e0', p: 2, flexShrink: 0 }}>
                    <Box display="flex" justifyContent="center">
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<SendIcon />}
                            onClick={handleSubmit}
                            disabled={saving}
                            sx={{ minWidth: 200 }}
                        >
                            {saving ? 'Submitting...' : 'Submit'}
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Modals */}
            <SignatureModalWithAuto
                open={!!signatureField}
                onClose={() => setSignatureField(null)}
                sigPadRef={sigPadRef}
                employeeName={signatureField ? form[`${signatureField}Name`] : ''}
                onAccept={handleSignatureAccept}
                existingSignature={signatureField ? form[`${signatureField}Signature`] : null}
            />

            <SelectSingleEmployeeModal
                open={!!employeeTarget}
                onClose={() => setEmployeeTarget(null)}
                onSelect={handleEmployeeSelect}
                title={employeeTarget === 'welder' ? 'Select Welder' : 'Select QC/CWI'}
            />
        </Box>
    );
};

export default WelderQualificationReleaseForm;
